import { Dispatch, HTMLProps, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./dropdown-multi.scss";
import { P2Button, P2TextInput, P2TextLabel } from "react-bootstrap-common-script";
import { DropdownMultiItem } from "./dropdown-multi-item";
import ChevronDown from "../../assets/svg/chevron-down";
import { DropdownItemInterface } from "../../models/interfaces/dropdown-item.interface";
import SearchIcon from "../../assets/svg/search-icon";
import { CrossIcon } from "../../assets/svg";

type DropdownMultiProps = HTMLProps<HTMLDivElement> & {
  items: DropdownItemInterface[];
  itemValues: string[];
  label: string;
  placeholder: string;
  required: boolean;
  tooltipContent?: string | JSX.Element;
  setItemValues: Dispatch<React.SetStateAction<string[]>>;
  identifierKey?: "title" | "code" | "value";
  disabledValues?: string[];
};

const DropdownMulti = ({
  items,
  itemValues,
  label,
  placeholder,
  required,
  tooltipContent,
  identifierKey = "code",
  setItemValues,
  disabledValues,
  ...props
}: DropdownMultiProps) => {
  // States
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);

  // Derived values
  const hasTooltip = tooltipContent !== null && tooltipContent !== undefined;
  const numberOfSelectedItems = itemValues.filter((itemBollean) => itemBollean).length;

  // Functions
  const handleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  function createHandleSelect(index: number, value: string) {
    return () => {
      setItemValues((prev) => {
        const current = [...prev];

        let findIndex = current.findIndex((e) => e === value);
        if (findIndex !== -1) {
          current.splice(findIndex, 1);
        } else {
          current.push(value);
        }
        return [...current];
      });
    };
  }

  const handleClickOutside = useCallback((event: MouseEvent) => {
    // if(selectAllRef.current && selectAllRef.current.contains(event.target as Node)){
    //   console.log("selectAllRef.current",selectAllRef.current, event.target);
    // }
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsExpanded(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [dropdownRef, selectAllRef]);

  const isAllSelected = useMemo(() => {
    if (itemValues.length > 0) {
      return items.length === itemValues.length;
    } else {
      return false;
    }
  }, [itemValues, items]);

  const toggleSelectAllDocuments = useCallback(() => {
    setSearchTerm("");
    if (isAllSelected) {
      setItemValues([]);
    } else {
      setItemValues(items.map((e) => e[identifierKey]));
    }
  }, [items, isAllSelected]);

  const filteredItems = useMemo(() => {
    return items.filter((e) => e.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()));
  }, [items, searchTerm]);

  return (
    <div className="polymer-dropdown__container" {...props}>
      {label && (
        <P2TextLabel
          bold={props.style?.fontWeight}
          marginBottom="10px"
          label={label}
          hasTooltip={hasTooltip}
          required={required}
          tooltipContent={tooltipContent}
        />
      )}

      <div ref={dropdownRef} className="polymer-dropdown__wrapper">
        <button className="polymer-dropdown__expand-button" onClick={handleExpand} style={{ boxShadow: isExpanded ? "0px 0px 2px 4px #64C8C880" : "" }}>
          <span className={`polymer-dropdown__expand-button-text ${numberOfSelectedItems === 0 ? "default" : ""}`}>
            {numberOfSelectedItems === 0 ? placeholder : `${numberOfSelectedItems} item(s) selected`}
          </span>
          <div className="polymer-dropdown__expand-button-chevron-wrapper" style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>
            <ChevronDown color="#0C8188" />
          </div>
        </button>
        {isExpanded && (
          <div className="polymer-dropdown__items-container" style={isExpanded ? { marginTop: 4 } : { height: 0, overflow: "hidden" }}>
            <div ref={searchBarRef} className="polymer-dropdown__search-bar">
              <P2TextInput placeholder="Type to search" type="text" value={searchTerm} onChange={setSearchTerm} />
              <SearchIcon size={24} fillColor="#0C8188" />
              {/* <CrossIcon size={24} fillColor="#0C8188"/> */}
            </div>
            {!searchTerm ? (
              <div onClick={toggleSelectAllDocuments} className="polymer-dropdown__select-all">
                <p ref={selectAllRef}>{!isAllSelected ? "Select all" : "Deselect all"}</p>
              </div>
            ) : null}
            {filteredItems
              .filter((e) => e.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()))
              .map((item, index) => {
                const isDisabled = disabledValues && disabledValues.includes(item[identifierKey]);

                return (
                  <DropdownMultiItem
                    handleChange={createHandleSelect(index, item[identifierKey])}
                    title={item.title}
                    isChecked={itemValues.includes(item[identifierKey])}
                    id={item.id}
                    disabled={isDisabled}
                  />
                );
              })}
            {filteredItems.filter((e) => e.title.includes(searchTerm)).length === 0 && <p>No search result...</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default DropdownMulti;
